import React from "react";

import * as types from "types";
import { setLastSelectedPersonaId } from "utilities/lastSelectedPersonaId";
import { useCurrentThread } from "./useCurrentThread";

export type UseCurrentPersonaReturn = readonly [
  types.Persona,
  (newPersona: types.Persona) => void
];

/**
 * Persona ids are `number | string` on the frontend but the DB API stores
 * them as TEXT, so a numeric id like `5` comes back as `"5"`. Numeric
 * strings get re-narrowed to numbers; non-numeric strings (UUID-style
 * custom-persona ids) pass through unchanged.
 */
function normalizePersonaId(id: types.Persona["id"]): types.Persona["id"] {
  if (typeof id !== "string") return id;

  const asNumber = Number(id);
  return Number.isNaN(asNumber) ? id : asNumber;
}

/**
 * Reads the persona for the current thread from the React Query cache and
 * returns a setter that persists changes via PATCH /threads/:id.
 */
export function useCurrentPersona(threadId: string): UseCurrentPersonaReturn {
  const [thread, , commitThread] = useCurrentThread(threadId);

  const personaId = normalizePersonaId(thread.personaId);
  const persona = types.getPersonaById(personaId);

  const setPersona = React.useCallback(
    (newPersona: types.Persona) => {
      commitThread((prev) => ({ ...prev, personaId: newPersona.id }));
      setLastSelectedPersonaId(newPersona.id);
    },
    [commitThread]
  );

  return [persona, setPersona] as const;
}
